import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import type {
  ACLEngine,
  EvaluationResult,
  PermissionAction,
  ResourceType,
  User,
} from "@shield-acl/core";

/**
 * Valor do contexto ACL
 */
export interface ACLContextValue<TContext = any> {
  engine: ACLEngine<TContext>;
  user: User<TContext> | null;
  setUser: (user: User<TContext> | null) => void;
  can: (
    action: PermissionAction,
    resource?: ResourceType,
    context?: TContext,
  ) => boolean;
  evaluate: (
    action: PermissionAction,
    resource?: ResourceType,
    context?: TContext,
  ) => EvaluationResult;
}

/**
 * Context do ACL
 */
const ACLContext = createContext<ACLContextValue | undefined>(undefined);

/**
 * Props do Provider
 */
export interface ACLProviderProps<TContext = any> {
  engine: ACLEngine<TContext>;
  user?: User<TContext> | null;
  children: React.ReactNode;
}

/**
 * Provider do ACL para React
 */
export function ACLProvider<TContext = any>({
  engine,
  user: initialUser = null,
  children,
}: ACLProviderProps<TContext>) {
  const [user, setUser] = useState<User<TContext> | null>(initialUser);

  /**
   * Verifica se usuário tem permissão
   */
  const can = useCallback(
    (
      action: PermissionAction,
      resource?: ResourceType,
      context?: TContext,
    ): boolean => {
      if (!user) return false;
      return engine.can(user, action, resource, context);
    },
    [engine, user],
  );

  /**
   * Avalia permissão com detalhes
   */
  const evaluate = useCallback(
    (
      action: PermissionAction,
      resource?: ResourceType,
      context?: TContext,
    ): EvaluationResult => {
      if (!user) {
        return {
          allowed: false,
          reason: "No user authenticated",
        };
      }
      return engine.evaluate(user, action, resource, context);
    },
    [engine, user],
  );

  /**
   * Valor do contexto memoizado
   */
  const value = useMemo<ACLContextValue<TContext>>(
    () => ({
      engine,
      user,
      setUser,
      can,
      evaluate,
    }),
    [engine, user, can, evaluate],
  );

  return <ACLContext.Provider value={value}>{children}</ACLContext.Provider>;
}

/**
 * Hook para acessar o contexto ACL
 */
export function useACLContext<TContext = any>(): ACLContextValue<TContext> {
  const context = useContext(ACLContext);

  if (!context) {
    throw new Error("useACLContext must be used within an ACLProvider");
  }

  return context as ACLContextValue<TContext>;
}
